/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { Box, Center, QrCode } from 'chakra-v3';

interface HelpQrCodeSectionProps {
  qrCodeValue?: string;
}

/**
 * HelpQrCodeSection component displays a QR code for WhatsApp chatbot access.
 * The QR code is rendered within a styled container with borders and padding.
 * If no QR code value is provided, an empty QR code will be displayed.
 *
 * @param qrCodeValue - The WhatsApp chatbot URL to encode in the QR code
 *
 * @example
 * ```tsx
 * <HelpQrCodeSection qrCodeValue="https://wa.me/1234567890" />
 * ```
 */
const HelpQrCodeSection: React.FC<HelpQrCodeSectionProps> = ({ qrCodeValue }) => (
  <Box display="flex" alignItems="center" justifyContent="center">
    <Box
      w="100%"
      h="100%"
      border="1px solid"
      borderColor="border.tealGradient1"
      borderRadius="8px"
      p={{ base: 3, md: 4 }}
    >
      <Center w="100%" h="100%" bg="bg.light" borderRadius="6px" p={{ base: 3, md: 4 }}>
        <QrCode.Root
          value={qrCodeValue ?? ''}
          size="full"
          boxShadow="none"
          aria-label={qrCodeValue ? 'Scan this QR code to open WhatsApp chatbot' : 'QR code not available'}
        >
          <QrCode.Frame>
            <QrCode.Pattern />
          </QrCode.Frame>
        </QrCode.Root>
      </Center>
    </Box>
  </Box>
);

export default HelpQrCodeSection;
